import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { profileKeys } from '@/features/profile';
import { unwrap } from '@/lib/result';

import { adminRepository } from '../api/admin.repository';

export const adminKeys = {
  all: ['admin'] as const,
  mapMasjids: () => [...adminKeys.all, 'map', 'masjids'] as const,
  mapRequests: () => [...adminKeys.all, 'map', 'requests'] as const,
  stats: () => [...adminKeys.all, 'stats'] as const,
  audit: (limit: number) => [...adminKeys.all, 'audit', limit] as const,
};

export function useSetVerified() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { profileId: string; verified: boolean }) =>
      unwrap(adminRepository.setVerified(vars.profileId, vars.verified)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
      void queryClient.invalidateQueries({ queryKey: adminKeys.stats() });
    },
  });
}

export function useSetCertifier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { profileId: string; isCertifier: boolean }) =>
      unwrap(adminRepository.setCertifier(vars.profileId, vars.isCertifier)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useMapMasjids() {
  return useQuery({
    queryKey: adminKeys.mapMasjids(),
    queryFn: () => unwrap(adminRepository.listMasjidsForMap()),
  });
}

export function useMapRequests() {
  return useQuery({
    queryKey: adminKeys.mapRequests(),
    queryFn: () => unwrap(adminRepository.listRequestsForMap()),
  });
}

export function useDashboardStats() {
  return useQuery({
    queryKey: adminKeys.stats(),
    queryFn: () => unwrap(adminRepository.dashboardStats()),
  });
}

export function useAuditLog(limit = 200) {
  return useQuery({
    queryKey: adminKeys.audit(limit),
    queryFn: () => unwrap(adminRepository.listAuditLog(limit)),
  });
}
